import React, { useEffect, useRef, useState } from "react";
import { Button, Select, Space, Spin } from "antd";
import { Tokens, useAuth } from "@/context/AuthContext";
import { Organization, SwitchOrganizationResponse, useOrganizations } from "@/utility/useOrganizations";
import { readDocumentCustomProperty } from "@/utility/documentCustomProperties";
import Logger from "@/utility/logger";
import ProcessingButton from "@/components/ui/ProcessingButton";
import { userSignedOut } from "@/auth/officeAccount";

interface AuthPageProps {
  onSignIn: () => void;
  version: string;
  organizationSelectionOnly?: boolean;
  /** Shown on the login form when it opens, e.g. after the session ended. */
  notice?: string | null;
}

interface LoginFormProps {
  error: string | null;
  loading: boolean;
  onMicrosoftSignIn: () => Promise<void>;
  onOtherAccountSignIn: () => Promise<void>;
}

interface OrganizationPickerProps {
  error: string | null;
  organizations: Organization[];
  accessToken: string | null;
  switchOrganization: (organizationId: string, jwtToken: string) => Promise<SwitchOrganizationResponse>;
  setTokens: React.Dispatch<React.SetStateAction<Tokens>>;
  setOrganizationId: (orgId: string | null) => void;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  onSignIn: () => void | Promise<void>;
}

const LoginForm: React.FC<LoginFormProps> = ({ error, loading, onMicrosoftSignIn, onOtherAccountSignIn }) => {
  return (
    <div className="signin_panel">
      {error && <div className="signin_error_alert">{error}</div>}
      <ProcessingButton
        onClick={onMicrosoftSignIn}
        isProcessing={loading}
        renderButton={({ onClick, disabled, className, isProcessing }) => (
          <Button className={`${className} signin_ms_button`} disabled={disabled} onClick={onClick}>
            <span className="signin_ms_content">
              {!isProcessing && <img src="/assets/microsoft-logo.png" alt="Microsoft" className="signin_ms_logo" />}
              <span className={`signin_ms_text${isProcessing ? " ai-processing-label" : ""}`}>
                {isProcessing ? "Signing in..." : "Sign in with Microsoft"}
              </span>
            </span>
          </Button>
        )}
      />
      {/* Word may be signed in with a different account than the one used
          for Jenly; this always opens the sign-in window. */}
      <Button type="link" className="signin_other_account" disabled={loading} onClick={onOtherAccountSignIn}>
        Sign in with a different account
      </Button>
    </div>
  );
};

const OrganizationPicker: React.FC<OrganizationPickerProps> = ({
  error,
  organizations,
  accessToken,
  switchOrganization,
  setTokens,
  setOrganizationId,
  setError,
  onSignIn,
}) => {
  const [selectedOrganization, setSelectedOrganization] = useState<string>("");
  const [switchingOrg, setSwitchingOrg] = useState(false);

  const handleOrganizationSwitch = async () => {
    if (!selectedOrganization) return;

    setSwitchingOrg(true);
    setError(null);

    try {
      const response = await switchOrganization(selectedOrganization, accessToken || "");
      if (response.access_token || response.refresh_token) {
        setTokens((prev) => ({
          accessToken: response.access_token ?? prev.accessToken,
          refreshToken: typeof response.refresh_token !== "undefined" ? response.refresh_token : prev.refreshToken,
        }));
      }
      setOrganizationId(selectedOrganization);
      const selectedOrg = organizations.find((org) => org.id === selectedOrganization);

      Logger.setContext({
        organizationId: selectedOrganization,
        organizationName: selectedOrg?.name || "",
      });
      Logger.trace("Organization selected", {
        action: "org_selection",
        orgId: selectedOrganization,
        orgName: selectedOrg?.name,
      });

      await onSignIn();
    } catch (error) {
      Logger.error("AuthPage.tsx: Failed to switch organization");
      setError("Failed to select organization. Please try again.");
    } finally {
      setSwitchingOrg(false);
    }
  };

  return (
    <div className="signin_org_picker">
      {error && <div className="signin_error_alert">{error}</div>}

      <div className="signin_org_title">Select an organization</div>
      <Select
        showSearch
        placeholder="Select an organization"
        optionFilterProp="label"
        onChange={setSelectedOrganization}
        disabled={switchingOrg}
        options={organizations.map((org) => ({
          value: org.id,
          label: org.name,
        }))}
        className="signin_org_select"
      />
      <div>
        <ProcessingButton
          onClick={handleOrganizationSwitch}
          isProcessing={switchingOrg}
          frameClassName="signin_continue_processing_frame"
          renderButton={({ onClick, disabled, className, isProcessing }) => (
            <Button
              className={`${className} signin_continue_button`}
              onClick={onClick}
              disabled={disabled || !selectedOrganization}
            >
              <Space size={6}>
                <img
                  src="/assets/icon-32.png"
                  alt="Jenly Icon"
                  style={{ width: "20px", height: "20px" }}
                />
                <span className={isProcessing ? "ai-processing-label" : undefined}>
                  {isProcessing ? "Continuing..." : "Continue"}
                </span>
              </Space>
            </Button>
          )}
        />
        <p
          style={{
            color: "#d1d1d1",
            fontSize: "10px",
            textAlign: "center",
            margin: 0,
            marginTop: "6px",
            marginBottom: "12px",
          }}
        >
          AI-generated content may be incorrect
        </p>
      </div>
    </div>
  );
};

const AuthPage: React.FC<AuthPageProps> = ({ onSignIn, organizationSelectionOnly = false, notice = null }) => {
  const { entraLogin, officeAccountLogin, tokens, setTokens, setOrganizationId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(notice);
  const [isLoggedIn, setIsLoggedIn] = useState(organizationSelectionOnly);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [skipOrganizationPicker, setSkipOrganizationPicker] = useState(false);

  const { getOrganizations, switchOrganization } = useOrganizations();

  useEffect(() => {
    if (!organizationSelectionOnly || !tokens.accessToken) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    getOrganizations(tokens.accessToken)
      .then((response) => {
        if (!cancelled) {
          setOrganizations(response.items.map((item) => item.organization));
          setIsLoggedIn(true);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Failed to load organizations.";
          setError(message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // The organization API helper is recreated by its hook on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationSelectionOnly, tokens.accessToken]);

  // Sign in with the Office account as soon as the add-in opens, unless the
  // user signed out last time.
  const autoSignInTried = useRef(false);
  useEffect(() => {
    if (organizationSelectionOnly || autoSignInTried.current || userSignedOut()) return;
    autoSignInTried.current = true;
    void handleMicrosoftSignIn("silent");
    // Once, when the sign-in page first shows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "silent": the automatic try when the add-in opens; never shows a window,
  // and leaves the button if it cannot sign in. "dialog": a different account.
  const handleMicrosoftSignIn = async (mode: "default" | "dialog" | "silent" = "default") => {
    setLoading(true);
    // A silent try that cannot sign in keeps any notice, e.g. session ended.
    if (mode !== "silent") setError(null);

    try {
      const result =
        mode === "silent" ? await officeAccountLogin() : await entraLogin({ forceDialog: mode === "dialog" });
      if (!result) {
        setLoading(false);
        return;
      }

      Logger.setContext({ userName: result.user_email, userId: result.user_email });
      Logger.trace("Entra login successful", {
        action: "entra_login",
        username: result.user_email,
      });

      // Fetch organizations (tokens are now set in AuthContext)
      const orgsResponse = await getOrganizations(result.access_token);
      setOrganizations(orgsResponse.items.map((item) => item.organization));

      // Auto-select if single org OR if the open document specifies a matching organization
      let matchingOrg: Organization | undefined;
      if (orgsResponse.items.length === 1) {
        matchingOrg = orgsResponse.items[0].organization;
      } else if (orgsResponse.items.length > 1) {
        const docOrgId = await readDocumentCustomProperty("selectedOrganizationId");
        if (docOrgId) {
          matchingOrg = orgsResponse.items.find(
            (item) => item.organization.id.toLowerCase() === String(docOrgId).toLowerCase()
          )?.organization;
        }
      }

      if (matchingOrg) {
        setSkipOrganizationPicker(true);
        const orgId = matchingOrg.id;
        const response = await switchOrganization(orgId, result.access_token);
        if (response.access_token || response.refresh_token) {
          setTokens((prev) => ({
            accessToken: response.access_token ?? prev.accessToken,
            refreshToken: typeof response.refresh_token !== "undefined" ? response.refresh_token : prev.refreshToken,
          }));
        }
        setOrganizationId(orgId);

        Logger.setContext({
          organizationId: orgId,
          organizationName: matchingOrg.name,
        });
        Logger.trace("Organization auto-selected", {
          action: "org_selection",
          orgId,
          orgName: matchingOrg.name,
        });

        await onSignIn();
        setLoading(false);
        setIsLoggedIn(true);
        return;
      }

      setLoading(false);
      setIsLoggedIn(true);
    } catch (err: any) {
      const message =
        err instanceof Error ? err.message : typeof err === "string" ? err : JSON.stringify(err) || "Sign-in failed";
      if (message !== "Sign-in cancelled") {
        setError(message);
        Logger.error("AuthPage.tsx: Entra login failed", { error: message, stack: err?.stack });
      }
      setLoading(false);
    }
  };

  return (
    <div className="wrapper_signin">
      <div className="flex flex-col items-center justify-center h-full w-full">
        {!isLoggedIn && (
          <div className="signin_header">
            <div className="signin_brand_card">
              <img src="/assets/brand-loader.svg" alt="Jenly" className="signin_logo" />
              <div className="signin_brand_name">JENLY.AI</div>
              <div className="signin_brand_tagline">YOUR AI-POWERED REGULATORY ASSISTANT</div>
            </div>
          </div>
        )}

        {!isLoggedIn && <LoginForm
            error={error}
            loading={loading}
            onMicrosoftSignIn={() => handleMicrosoftSignIn()}
            onOtherAccountSignIn={() => handleMicrosoftSignIn("dialog")}
          />}
        {organizationSelectionOnly && loading && organizations.length === 0 && (
          <div className="signin_org_loading" aria-live="polite">
            <Spin size="small" />
            <span>Loading organizations...</span>
          </div>
        )}
        {organizationSelectionOnly && error && !loading && organizations.length === 0 && (
          <div className="signin_panel">
            <div className="signin_error_alert">{error}</div>
          </div>
        )}
        {isLoggedIn && organizations.length > 0 && !skipOrganizationPicker && !loading && (
          <OrganizationPicker
            error={error}
            organizations={organizations}
            accessToken={tokens.accessToken}
            switchOrganization={switchOrganization}
            setTokens={setTokens}
            setOrganizationId={setOrganizationId}
            setError={setError}
            onSignIn={onSignIn}
          />
        )}
      </div>
    </div>
  );
};

export default AuthPage;
